import { Tooltip, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import HeaderFilterToolTip from '../../DashboardCustomers/HeaderFilter/HeaderFilterToolTip';
import icons from 'constants/icons';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadAllBOBCommissionRecoData } from 'state/actions/healthSherpa';
import { formatMoney } from 'utils/utils';
import style from '../DashboardCommissionReco.module.scss';

const CommissionRecoTable = ({
  data_for_table,
  policies_records_count,
  setCurrentPageSize,
  setCurrentPage,
  currentPage,
  formData,
  setTriggerFetch,
  handleChangeSelectValue,
  applyFilters
}) => {
  const { isDownloadingReport } = useSelector(state => state.commissions);
  const dispatch = useDispatch();

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 50
  });

  useEffect(() => {
    if (currentPage - 1 !== pagination.pageIndex) {
      setPagination(current => {
        return {
          ...current,
          pageIndex: currentPage - 1
        };
      });
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPage - 1 !== pagination.pageIndex) {
      setTriggerFetch(current => current + 1);
      setCurrentPage(pagination.pageIndex + 1);
    }
  }, [pagination.pageIndex]);

  useEffect(() => {
    setTriggerFetch(current => current + 1);
    setCurrentPageSize(pagination.pageSize);
  }, [pagination.pageSize]);

  const handleDownloadRows = useCallback(() => {
    dispatch(
      downloadAllBOBCommissionRecoData({
        ...formData
      })
    );
  }, [formData]);

  const columns = useMemo(
    () => [
      {
        header: 'Client',
        accessorKey: 'holder',
        enableResizing: false,
        size: 150,
        Filter: ({}) => {
          const options = ['Active', 'Inactive'];

          return (
            <HeaderFilterToolTip
              forceUpdateProp={formData}
              applyFilters={applyFilters}
              initialData={formData.agentStatus}
              clearFilters={() => handleChangeSelectValue([], 'agentStatus')}
              statusName='client'
            >
              <div
                style={{
                  minWidth: '200px',
                  padding: '16px 16px 0px 16px'
                }}
              >
                <FormGroup>
                  {options.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={formData.agentStatus.includes(option)}
                        />
                      }
                      label={option}
                      onChange={() =>
                        handleChangeSelectValue(
                          formData.agentStatus.includes(option)
                            ? formData.agentStatus.filter(o => o !== option)
                            : [...formData.agentStatus, option],
                          'agentStatus'
                        )
                      }
                    />
                  ))}
                </FormGroup>
              </div>
            </HeaderFilterToolTip>
          );
        },
        flexGrow: 1,
        /* eslint react/prop-types: 0 */
        Cell: ({ cell }) => {
          const { row } = cell;
          const { original } = row;

          return (
            <div
              style={{
                color: '#2564B3',
                fontWeight: '600'
              }}
            >
              {original.holder}
            </div>
          );
        }
      },
      {
        header: 'Carrier',
        accessorKey: 'carriername',
        size: 120,
        enableColumnFilter: false
      },
      {
        header: 'Policy #',
        accessorKey: 'carrierpolicynbr',
        size: 140,
        enableColumnFilter: false
      },
      {
        header: 'State',
        accessorKey: 'holderstate',
        size: 70,
        enableColumnFilter: false,
        enableResizing: false
      },
      {
        header: 'NPN',
        accessorKey: 'writingagentnpnfrombob',
        size: 90,
        enableResizing: false,
        enableColumnFilter: false
      },
      {
        header: 'Effective Date',
        accessorKey: 'effectivedatefrombob',
        size: 130,
        enableResizing: false,
        enableColumnFilter: false
      },
      ...formData.initial_date.map((date, index) => ({
        header: date,
        noPadding: true,
        accessorKey: `commission${index}`,
        enableColumnFilter: false,
        enableResizing: false,
        Cell: ({ cell }) => {
          const { row } = cell;
          const { original } = row;

          // 01-2024
          const coverageMonth = date.split('-');
          const matchedCommissions = original.commission.filter(
            commission =>
              commission.CoverageMonth ===
              `${coverageMonth[0].replace(/\b0+/g, '')}/${coverageMonth[1]}`
          );

          // 01/01/2024
          const effectivedatefrombob = new Date(original.effectivedatefrombob);
          const coverageDate = new Date(
            `${coverageMonth[0].replace(/\b0+/g, '')}/01/${coverageMonth[1]}`
          );

          const Inactive = effectivedatefrombob > coverageDate;

          let noPayment = false;

          if (
            formData.initial_date[index + 1] &&
            formData.initial_date[index - 1]
          ) {
            // Next Month matches
            const nextCovMonth = formData.initial_date[index + 1].split('-');
            const nextMonth = original.commission.some(
              commission =>
                commission.CoverageMonth ===
                `${nextCovMonth[0].replace(/\b0+/g, '')}/${nextCovMonth[1]}`
            );

            // Prev Month matches
            const prevCovMonth = formData.initial_date[index - 1].split('-');
            const prevMonth = original.commission.some(
              commission =>
                commission.CoverageMonth ===
                `${prevCovMonth[0].replace(/\b0+/g, '')}/${prevCovMonth[1]}`
            );

            noPayment = !Inactive && nextMonth && prevMonth;
          }

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              {matchedCommissions.length === 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: Inactive
                      ? '#909090'
                      : noPayment
                      ? 'red'
                      : '#A1BFCD',
                    borderRadius: '13px',
                    padding: '2px 8px',
                    color: 'white'
                  }}
                >
                  {Inactive && <>Inactive</>}
                  {noPayment && <>No Payment</>}
                  {!Inactive && !noPayment && <>Pending</>}
                </div>
              )}

              {matchedCommissions.map(commission => {
                const ChargeBack = commission.Payable < 0;

                const NotPaid = commission.Payable === 0;

                const Paid = commission.Payable > 0;
                const Value = ChargeBack
                  ? 'ChargeBack'
                  : NotPaid
                  ? 'NotPayment'
                  : Paid
                  ? 'Paid'
                  : '';

                return (
                  <CustomCells
                    key={commission.id}
                    Small={true}
                    Value={Value}
                    DateString={
                      commission.ONLStatementDate + ' $' + commission.Payable
                    }
                    TooltipContent={
                      <>
                        <p>Payee: {commission.PayeeName}</p>
                        <p>Product: {commission.Product}</p>
                        <p>Rate: {formatMoney(Number(commission.Rate))}</p>
                        <p>Members: {commission.MemberCount}</p>
                        <p>Writing Agent NPN: {commission.WritingAgentNPN}</p>
                        <p>Writing Agent Name: {commission.WritingAgentName}</p>
                      </>
                    }
                  />
                );
              })}
            </div>
          );
        }
      }))
    ],
    [formData]
  );

  const table = useMaterialReactTable({
    columns,
    data: [...data_for_table],
    enableColumnActions: false,
    enableColumnResizing: true,
    enableTopToolbar: false,
    enableSorting: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    initialState: { showColumnFilters: true },
    paginationDisplayMode: 'pages',
    rowCount: Number(policies_records_count),
    onPaginationChange: setPagination,
    state: {
      pagination: pagination
    },
    muiPaginationProps: {
      rowsPerPageOptions: [50, 100],
      showFirstButton: false,
      showLastButton: false,
      variant: 'outlined',
      style: {
        paddingRight: '60px'
      }
    },
    muiTablePaperProps: {
      elevation: 0, //change the mui box shadow
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 0
      },
      className: style.tableHeight
    },
    muiTableContainerProps: {
      id: 'customers-bob-reco-table-body',
      style: {
        flex: 1,
        scrollbarWidth: 'auto'
      }
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: ''
      }
    },
    renderBottomToolbarCustomActions: ({}) => (
      <Button
        isDisabled={isDownloadingReport}
        text='Export All'
        variant='secondary'
        onClick={handleDownloadRows}
        isLoading={isDownloadingReport}
      />
    ),
    muiTableHeadCellProps: {
      style: {
        backgroundColor: '#FAFAFA',
        padding: '10px 5px',
        justifyContent: 'center'
      }
    },
    muiTableBodyCellProps: {
      style: {
        padding: '10px 5px'
      }
    },
    manualPagination: true,
    renderEmptyRowsFallback: () => (
      <h2
        style={{
          color: 'grey',
          textAlign: 'center',
          fontWeight: '400',
          fontSize: 14,
          padding: 15
        }}
      >
        No policies found
      </h2>
    ),
    enableExpanding: false,
    enableExpandAll: false,
    muiTableBodyCellProps: ({ column }) => {
      const { columnDef } = column;
      if (columnDef.id === 'mrt-row-spacer')
        return {
          style: {
            maxWidth: '0px'
          }
        };
      return {
        className: style[`react-table-column-flex-grow-${columnDef.flexGrow}`],
        ...(columnDef.noPadding
          ? {
              style: {
                padding: '5px 5px'
              }
            }
          : {})
      };
    },
    muiTableHeadCellProps: ({ column }) => {
      const { columnDef } = column;

      const hasFilters = columnDef.Filter;

      if (columnDef.id === 'mrt-row-spacer')
        return {
          style: {
            maxWidth: '0px'
          }
        };
      return {
        className: `${
          style[`react-table-column-flex-grow-${columnDef.flexGrow}`]
        } ${hasFilters ? style['react-table-head-content'] : ''}`,
        style: {
          display: 'flex',
          flexDirection: 'row',
          gap: '10px'
        }
      };
    },
    muiDetailPanelProps: () => ({
      sx: () => ({
        backgroundColor: 'rgba(227,246,255,1)'
      })
    })
  });

  return (
    <div
      style={{
        width: '100%',
        flex: 1,
        display: 'flex'
      }}
    >
      <MaterialReactTable table={table} />
    </div>
  );
};

CommissionRecoTable.propTypes = {
  data_for_table: PropTypes.array,
  policies_records_count: PropTypes.number,
  setCurrentPage: PropTypes.func,
  setCurrentPageSize: PropTypes.func,
  currentPage: PropTypes.number,
  setTriggerFetch: PropTypes.func,
  formData: PropTypes.object,
  handleChangeSelectValue: PropTypes.func,
  applyFilters: PropTypes.func
};

export default memo(CommissionRecoTable);

const CircleIcon = icons['circle'];
const WarningIcon = icons['warning'];

const CustomCellsTop = ({
  Value,
  DateString,
  Small,
  TooltipContent = <></>
}) => {
  const colorIconConfig = {
    NotPayment: {
      color: '#E85C53',
      icon: <WarningIcon fontSize='small' />,
      text: 'Not Paid'
    },
    Paid: {
      color: '#5DBB29',
      icon: <CircleIcon fontSize='small' />,
      text: 'Paid'
    },
    ChargeBack: {
      color: '#E4D00A',
      icon: <WarningIcon fontSize='small' />,
      text: 'Charged Back'
    }
  };

  return (
    <Tooltip title={TooltipContent}>
      <div
        style={{
          color: colorIconConfig[Value]?.color ?? '',
          padding: '0',
          margin: '0',
          lineHeight: '1',
          borderRadius: '13px',
          display: 'flex',
          alignItems: 'center',
          ...(Small
            ? {
                fontSize: '12px',
                fontWeight: 500,
                gap: '4px'
              }
            : {
                height: '24px',
                width: '100%',
                gap: '10px'
              })
        }}
      >
        {colorIconConfig[Value]?.icon}
        {!Small && (colorIconConfig[Value]?.text ?? '')}
        {' ' + DateString}
      </div>
    </Tooltip>
  );
};

const CustomCells = memo(CustomCellsTop);

CustomCells.propTypes = {
  Value: PropTypes.string,
  Date: PropTypes.string,
  Small: PropTypes.bool
};
